import { ReactElement } from 'react';

import { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import { CompositeNavigationProp, NavigatorScreenParams, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { WalletType } from '@/feature/account-summary/hooks/use-wallet-history';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { BetslipPick, InvalidPicksByEvent, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { BetSlipParamsList } from '@/feature/betslip-sbk/types';
import { PaymentMethodType } from '@/feature/deposit/hooks/use-deposit-payment-methods';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';
import { KycProvider } from '@/feature/kyc/const';
import { SECTION_SCREENS } from '@/feature/lobby/utils/tabs/types';
import { UserLimitDuration } from '@/feature/responsible-gaming/hooks/types';
import { GameResult } from '@/feature/results-arcade/components/GameResultItem';
import { ClosedLoopMethodProps } from '@/feature/withdraw/components/closedLoop.types';
import { WithdrawMethod } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import { Product } from '@/hooks/use-jurisdiction';
import { League } from '@/types/api.generated';
import { RemovableCardType } from '@/types/removable-cards';
import { AppFeatureFlags } from '@/utils/parse-prismic-content';

import { ArcadeTabNavigatorParamList } from './arcade/types';
import { CasinoTabNavigatorParamList } from './casino/types';
import { PickemLobbyStackParamList, PickemTabNavigatorParamList } from './pickem/types';
import { PredictionTabNavigatorParamList } from './prediction/types';
import { RipsTabNavigatorParamList } from './rips/types';
import { SweepsTabNavigatorParamList } from './sweeps/types';

export enum IdpvSource {
    KYC = 'kyc',
    ACCOUNT_SETTING = 'account_setting',
    MANAGE_CARD = 'manage_card',
    WITHDRAW = 'withdraw',
}

export type IdpvSourceType = IdpvSource | undefined;

export type FantasyPickSlipStackParamList = {
    FantasyPickSlip: {
        shouldOpenLineupModal?: boolean;
    };
    SubmitEntry: {
        amount: number;
        picks: BetslipPick[];
        onInvalidProjectionsFound?: () => void;
        entry?: EntryInfoFragment;
        isTimeout?: boolean;
        isRaceToPlaceEntry?: boolean;
        isMysteryMode?: boolean;
        isFreeEntry?: boolean;
        multiplier?: string;
    };
};

export type BetSlipStackParamList = {
    BetSlipModal: NavigatorScreenParams<BetSlipParamsList>;
    BetSuccessScreen: undefined;
};

export type DepositStackParamList = {
    DepositScreen: {
        promoSlug?: string;
        shouldRedirectToLobby?: boolean;
        disableTransition?: boolean;
        forceDepositWithDebitCard?: boolean;
        isRedirectedFromVerificationSuccess?: boolean;
        suggestedRetryAmount?: string;
        shouldPopToLobby?: boolean;
    };
    DepositMethodWebViewModal: {
        selectedPaymentMethodType: PaymentMethodType;
        currency?: string;
        payment_token?: string;
        payment_type_id?: string;
    };
    MazoomaDepositWebViewModal: {
        currency?: string;
        payment_token?: string;
        payment_type_id?: string;
    };
    PayByBankDepositWebViewModal: {
        currency?: string;
    };
    VenmoDepositScreen: {
        currency?: string;
    };
    TrustlyDepositScreen: {
        currency?: string;
        payment_type_id?: string;
    };
    TrustlyFundsGuidance: {
        suggestedRetryAmount: string;
    };
    FantasyMoneyDepositScreen: {
        selectedPaymentMethodType: PaymentMethodType;
        currency?: string;
    };
    SocialWalletDepositScreen: {
        selectedPaymentMethodType: PaymentMethodType;
        currency?: string;
    };
    DepositStatusModal: {
        paymentProvider: PaymentMethodType;
        currentStatus: string;
    };
    SuccessfulDepositModal: {
        paymentProvider: PaymentMethodType;
        title?: string;
        description?: string;
        shouldRedirectToLobby?: boolean;
        disableTransition?: boolean;
        isRestrictedBin?: boolean;
    };
    FailedDepositModal: {
        paymentProvider: PaymentMethodType;
        errorCode?: string;
        showContactSupport?: boolean;
        description?: React.ReactNode;
    };
    AddDebitCardWebView: {
        paysafeAccountId: string;
        closeIconHandlePress?: () => void;
        canUse3ds: boolean;
    };
    VerifyingDebitDeposit: {
        currency?: string;
        payment_token?: string;
        payment_type_id?: string;
        shouldTrigger3ds?: boolean;
    };
    DepositSalvage: {
        options: AppFeatureFlags;
        paymentParams: {
            currency?: string;
            payment_token?: string;
            payment_type_id?: string;
        };
    };
    DepositExpiryUpdate: {
        currency?: string;
        payment_token?: string;
        payment_type_id?: string;
    };
    CoinPackageScreen: { isFromKyc?: boolean; isTrustlyKyc?: boolean } | undefined;
};

export type WithdrawStackParamList = {
    ChooseWithdrawalMethod: undefined;
    WithdrawForm: {
        selectedMethod: WithdrawMethod;
        methods: WithdrawMethod[];
    };
    WithdrawalExpiryUpdate: {
        selectedMethod: WithdrawMethod;
        methods: WithdrawMethod[];
    };
    SuccessfulWithdrawal: {
        amountToWithdraw: number;
        methodType?: string;
        title: string;
        description: string;
    };
    FailedWithdrawal: {
        errorCode?: string;
        selectedMethod?: WithdrawMethod;
    };
    FailedMultipleWithdrawal: {
        promisesResult: any[];
        distribution: ClosedLoopMethodProps[];
    };
    RedeemToFantasyFormScreen: undefined;
};

export type KycStackParamList = {
    KycSelector: undefined;
    KycSsn: undefined;
    KycFirstLastName: undefined;
    KycDob: undefined;
    KycHomeAddress: undefined;
    KycAcknowledgements: { provider?: KycProvider } | undefined;
    KycReviewPersonalInfo: undefined;
    KycVerificationFailed?: { hasIdScanFailed?: boolean; liveAccountEmail?: string };
    KycVerifyingIdentity: undefined;
    KycVerifyingIdpv: undefined;
    KycVerificationSuccess: undefined;
    KycSsnRetry: undefined;
    KycFirstLastNameReview: undefined;
    KycDobReview: undefined;
    KycHomeAddressReview: undefined;
    KycAcknowledgementsRetry: { provider?: KycProvider } | undefined;
    KycScanId?: { source?: IdpvSourceType };
    KycIdpvWebView: { uri: string };
    DefaultProductOnboarding: { kycProvider: KycProvider };
    TrustlyWebview:
        | {
              payment_type_id?: string;
              transactionId?: string;
              isTrustlyIdOnly?: boolean;
          }
        | undefined;
    TrustlyId: undefined;
    TrustlyNumpad: undefined;
    TrustlyKycStatusModal: {
        currentStatus: string;
    };
    VerificationSelector: undefined;
};

export type RootStackParamList = {
    Splash: {
        redirectPath?: string;
    };
    Launch: undefined;
    IneligibleLocationWeb: undefined;
    AllowLocation: { errorMode: 'countyDetect' | 'locationAccess'; onSelectUsState: (state: string) => void };
    Logout: undefined;
    Login?: {
        initialEmail: string | undefined;
        //code & session_state used on web for the redirect
        code: string | undefined;
        session_state: string | undefined;
        registering: string | undefined;
    };
    PickemHome: NavigatorScreenParams<PickemTabNavigatorParamList>;
    SweepsHome: NavigatorScreenParams<SweepsTabNavigatorParamList>;
    ArcadeHome: NavigatorScreenParams<ArcadeTabNavigatorParamList>;
    CasinoHome: NavigatorScreenParams<CasinoTabNavigatorParamList>;
    PredictionHome: NavigatorScreenParams<PredictionTabNavigatorParamList>;
    RipsHome: NavigatorScreenParams<RipsTabNavigatorParamList>;
    ArcadeGameWebView: { gameId: string; iframeUrl: string; isPractice: boolean; betExternalId?: string };
    RipsGameWebView: { path?: string; param?: string };
    ArcadeGameResults: { gameResult: GameResult };
    FantasyPickSlip: {
        shouldOpenLineupModal?: boolean;
    };
    FantasyPickSlipStack: NavigatorScreenParams<FantasyPickSlipStackParamList>;
    BetSlipStack: NavigatorScreenParams<BetSlipStackParamList>;
    DepositStack: NavigatorScreenParams<DepositStackParamList>;
    EditQuickAmounts: undefined;
    MysteryInfoModal: undefined;
    SbkBetScreen: { betId: string; bet?: Bet };
    BetShareScreen: { betId: string; bet?: Bet };
    Register:
        | {
              //code & session_state used on web for the redirect
              code: string | undefined;
              session_state: string | undefined;
          }
        | undefined;
    CancelEntry: {
        entryId: string;
        events: EventInfo[];
    };
    EntryScreen: { id: string };
    PushPrimer: undefined;
    EntryShare: {
        id?: string;
    };
    ErrorModal: {
        title: string;
        subtitle?: string;
        primaryButton: string | ReactElement;
        showPrimaryButton?: boolean;
        showSecondaryButton?: boolean;
        secondaryButton?: string;
        footer?: React.ReactNode;
        closeIconMode?: 'back' | 'close' | 'none';
        handlePress?: () => void;
        handleSecondaryButtonPress?: () => void;
        closeIconHandlePress?: () => void;
        showNavbarTitle?: boolean;
    };
    IneligibleStateModal: {
        title: string;
        subtitle?: string;
        primaryButton: string;
        showPrimaryButton?: boolean;
        showSecondaryButton?: boolean;
        secondaryButton?: string;
        footer?: React.ReactNode;
        handlePress?: () => void;
        handleSecondaryButtonPress?: () => void;
        closeIconHandlePress?: () => void;
        closeIconMode?: 'back' | 'close' | 'none';
        showNavbarTitle?: boolean;
    };
    PlayersRemovedModal: { invalidPicksByEvent: InvalidPicksByEvent[]; picksWithMissingEvents: BetslipPick[] };
    ModalWebView: { uri: string; title?: string };
    CasinoGameWebView: { gameId: string; gameParams?: { launchUrl: string; title?: string } };
    DeveloperScreen: undefined;
    DevDemoStack: NavigatorScreenParams<DevDemoStackParamList> | undefined;
    ProductSwitcher: undefined;
    IAPScreen: undefined;
    DepositLimitAdd: { duration: UserLimitDuration };
    WithdrawStack: NavigatorScreenParams<WithdrawStackParamList>;
    KycStack: NavigatorScreenParams<KycStackParamList>;
    DepositLimitEdit: { duration: UserLimitDuration; amount: number };
    MaxSingleEntryLimitEdit: { amount: number };
    EntryLimitAdd: { duration: UserLimitDuration };
    EntryLimitEdit: { duration: UserLimitDuration; amount: number };
    MaxSingleEntryLimitAdd: undefined;
    EditUsernameAndAvatar: undefined;
    AccountEditStack: NavigatorScreenParams<AccountEditStackParamList>;
    TimeoutAdd: undefined;
    AccountHome: NavigatorScreenParams<AccountStackParamList>;
    DefaultProductSelect: undefined;
    PromoDetails: { promoId: string };
    Feed: undefined;
    FantasySearch: undefined;
    SuccessModal: {
        title: string | React.ReactNode;
        subtitle?: string;
        additionalInfo?: React.ReactNode;
        primaryButton: string;
        closeIconMode?: 'back' | 'close' | 'none';
        closeIconHandlePress?: () => void;
        handlePress?: () => void;
        secondaryButton?: string;
        handleSecondaryPress?: () => void;
    };
    GuardBiometricsScreen: undefined;
    MaintenanceScreen: undefined;
    FeaturedLineupsScreen: { title: string };
    InviteFriends: undefined;
    InviteFriendsV2: undefined;
    ReferralHistory: undefined;
    ReferralTracking: { referral: any };
    FantasyEventsSheets: {
        // the id of the event to open
        eventId: string;
        // reanimated tries to reuse sharedValues which lead to weird behavior
        // to avoid this we use this key that changes every time we open the bottom sheets
        // this key is used in the FantasyEventTransitionProvider
        contextKey: string;
        // the league of the event - used to fetch the events info
        league: League;
        // optional - set when arriving via player profile deeplink
        playerId?: string;
    };
    SbkEventsSheets: {
        // the id of the event to open
        eventId: string;
        // reanimated tries to reuse sharedValues which lead to weird behavior
        // to avoid this we use this key that changes every time we open the bottom sheets
        // this key is used in the FantasyEventTransitionProvider
        contextKey: string;
        // the league of the event - used to fetch the events info
        leagueId: string;
    };
    PlayerProfileSheets: {
        // reanimated tries to reuse sharedValues which lead to weird behavior
        // to avoid this we use this key that changes every time we open the bottom sheets
        // this key is used in the FantasyEventTransitionProvider
        contextKey: string;
    };
    SinglePlayerProfileModalScreen: { player: PlayerWithTeamAndEvent };
    WhatsNewScreen: undefined;
    PickemAllLeaguesScreen: undefined;
    SbkAllLeaguesScreen: undefined;
};

export type PromohubStackParamList = {
    Promohub: { promoId?: string; mode: 'tabs' | 'stack' };
    PromoDetails: { promoId: string };
};

export type InviteFriendsStackParamList = {
    InviteFriends: { hideNavigationButton?: boolean } | undefined;
    ReferralHistory: undefined;
    //TODO: remove any and set type for referral
    ReferralTracking: { referral: any };
    InviteFriendsV2: undefined;
};

export type AccountStackParamList = {
    Account: { mode: 'tabs' | 'stack' };
    ResponsibleGaming: undefined;
    DepositLimits: undefined;
    LimitsHistory: undefined;
    EntryLimits: undefined;
    MaxSingleEntryLimits: undefined;
    RealityCheck: undefined;
    Timeout: undefined;
    HideVerticals: { navigateHomeOnBack?: boolean } | undefined;
    SelfExclusion: undefined;
    TransactionHistory: undefined;
    AccountDetails: undefined;
    YearEndStatement: undefined;
    AccountSummary: undefined;
    PlayHistory: { product: Product };
    WalletHistory: { wallet: WalletType };
    CasinoHistoryScreen: undefined;
    TransactionDetails: { userWalletId: string };
    SelfExclusionAdd: undefined;
    StateSelector: undefined;
    Preferences: undefined;
    SecondaryWallet: { product: Product };
    StateLimits: undefined;
    ManageDebitCards: undefined;
    ManageExpiredDebitCardDetail: { selectedMethod: RemovableCardType };
};

export type AccountEditStackParamList = {
    PhoneVerification: { title?: string; screenTitle?: string; redirectRoute?: string };
    PasswordEdit: undefined;
    EmailUpdate: undefined;
    EmailUpdateConfirm: { newEmail: string };
    PhoneNumberEditInitialVerification: undefined;
    PhoneNumberEdit: undefined;
    PhoneNumberEditUpdatedVerification: { phoneNumber: string };
    ManageDebitCardExpiryUpdate: { selectedMethod: RemovableCardType };
};

export type AccountEditStackNavigationProp<
    RouteName extends keyof AccountEditStackParamList = keyof AccountEditStackParamList
> = CompositeNavigationProp<
    NativeStackNavigationProp<AccountEditStackParamList, RouteName>,
    NativeStackNavigationProp<RootStackParamList>
>;

export type AccountEditStackScreenProps<RouteName extends keyof AccountEditStackParamList> = {
    navigation: AccountEditStackNavigationProp<RouteName>;
    route: RouteProp<AccountEditStackParamList, RouteName>;
};

export type SharedTabsParamList = {
    PromohubStack: NavigatorScreenParams<PromohubStackParamList>;
    AccountStack: NavigatorScreenParams<AccountStackParamList>;
};

export type AccountTabNavigationProp = CompositeNavigationProp<
    BottomTabNavigationProp<PickemTabNavigatorParamList, 'AccountStack'>,
    CompositeNavigationProp<
        NativeStackNavigationProp<AccountStackParamList>,
        NativeStackNavigationProp<RootStackParamList>
    >
>;

type RootStackScreenNames = keyof RootStackParamList;
type HomesStackScreenNames = keyof PickemLobbyStackParamList;
type PickSlipScreenNames = keyof FantasyPickSlipStackParamList;
type KycScreenNames = keyof KycStackParamList;

export type ScreenName =
    | RootStackScreenNames
    | HomesStackScreenNames
    | PickSlipScreenNames
    | KycScreenNames
    | SECTION_SCREENS;

declare global {
    namespace ReactNavigation {
        interface RootParamList extends RootStackParamList {}
    }

    // used to know which architecture we are using
    // false/undefined -> Paper UI Manager -> old architecture
    // true -> Fabric UI Manager -> new architecture
    var nativeFabricUIManager: boolean;
}
